import {
  ChatBubbleLeftIcon,
  CloudIcon,
  CodeBracketIcon,
} from '@heroicons/react/20/solid';
import { ChevronDown, Plus } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { FirmCombobox } from '@/components/firms/FirmCombobox';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  TemplateFormat,
} from '@/services/api/generated/templatesV2Service';
import {
  useTemplateCreateUnifiedMutation,
  useTemplateCreateWithFileMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

interface NewTemplateMenuProps {
  firmId?: string;
}

type PendingAction =
  | { type: 'file'; file: File }
  | { type: 'format'; format: TemplateFormat };

interface AnalysisError {
  phase: string;
  message: string;
  detail?: string;
}

export const NewTemplateMenu = ({ firmId }: NewTemplateMenuProps) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createTemplateMutation] = useTemplateCreateUnifiedMutation();
  const [createWithFile] = useTemplateCreateWithFileMutation();
  // Template validation error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [analysisErrors, setAnalysisErrors] = useState<AnalysisError[]>([]);
  const [errorTemplateName, setErrorTemplateName] = useState('');

  // Global scope warning state
  const [showGlobalWarning, setShowGlobalWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [selectedFirmId, setSelectedFirmId] = useState('');

  const navigateToTemplate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: any, navFirmId?: string) => {
      toast.success(`Template "${result.name}" created`);
      const path = `/templates-v2/${result.id}`;
      const ctxFirmId = navFirmId || firmId;
      if (ctxFirmId) {
        navigate(`${path}?firmContext=${encodeURIComponent(ctxFirmId)}`);
      } else {
        navigate(path);
      }
    },
    [firmId, navigate],
  );

  const executeCreateWithFile = useCallback(
    async (file: File, scopeFirmId?: string) => {
      setIsCreating(true);
      try {
        // Step 1: Validate file before creating
        const validateFormData = new FormData();
        validateFormData.append('file', file);
        const csrfToken =
          typeof document !== 'undefined'
            ? (document.cookie.match(/csrftoken=([^;]+)/)?.[1] ?? '')
            : '';

        try {
          const validateResponse = await fetch(
            `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/templates/validate/`,
            {
              method: 'POST',
              credentials: 'include',
              headers: { 'X-CSRFToken': csrfToken },
              body: validateFormData,
            },
          );

          if (validateResponse.ok) {
            const validation = await validateResponse.json();
            if (!validation.valid && validation.errors?.length > 0) {
              setErrorTemplateName(file.name);
              setAnalysisErrors(validation.errors);
              setShowErrorModal(true);
              return;
            }
          }
        } catch {
          // Validation endpoint failed — proceed with creation anyway
        }

        // Step 2: Create template (file validated)
        const result = await createWithFile({
          file,
          ...(scopeFirmId
            ? { scope: 'FIRM' as const, firm_id: scopeFirmId }
            : {}),
        }).unwrap();

        navigateToTemplate(result, scopeFirmId);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to create template';
        toast.error(message);
      } finally {
        setIsCreating(false);
      }
    },
    [createWithFile, navigateToTemplate],
  );

  const executeCreateFormat = useCallback(
    async (format: TemplateFormat, scopeFirmId?: string) => {
      setIsCreating(true);
      try {
        const result = await createTemplateMutation({
          body: {
            format,
            ...(scopeFirmId
              ? { scope: 'FIRM' as const, firm_id: scopeFirmId }
              : {}),
          },
        }).unwrap();
        navigateToTemplate(result, scopeFirmId);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to create template';
        toast.error(message);
      } finally {
        setIsCreating(false);
      }
    },
    [createTemplateMutation, navigateToTemplate],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Reset input so the same file can be re-selected
      event.target.value = '';

      if (firmId) {
        executeCreateWithFile(file, firmId);
      } else {
        setPendingAction({ type: 'file', file });
        setSelectedFirmId('');
        setShowGlobalWarning(true);
      }
    },
    [firmId, executeCreateWithFile],
  );

  const handleUploadDocument = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCreateFormat = useCallback(
    (format: TemplateFormat) => {
      if (firmId) {
        executeCreateFormat(format, firmId);
      } else {
        setPendingAction({ type: 'format', format });
        setSelectedFirmId('');
        setShowGlobalWarning(true);
      }
    },
    [firmId, executeCreateFormat],
  );

  const handleConfirmGlobal = useCallback(() => {
    if (!pendingAction) return;
    setShowGlobalWarning(false);
    if (pendingAction.type === 'file') {
      executeCreateWithFile(pendingAction.file);
    } else {
      executeCreateFormat(pendingAction.format);
    }
    setPendingAction(null);
  }, [pendingAction, executeCreateWithFile, executeCreateFormat]);

  const handleConfirmWithFirm = useCallback(() => {
    if (!pendingAction || !selectedFirmId) return;
    setShowGlobalWarning(false);
    if (pendingAction.type === 'file') {
      executeCreateWithFile(pendingAction.file, selectedFirmId);
    } else {
      executeCreateFormat(pendingAction.format, selectedFirmId);
    }
    setPendingAction(null);
    setSelectedFirmId('');
  }, [
    pendingAction,
    selectedFirmId,
    executeCreateWithFile,
    executeCreateFormat,
  ]);

  const handleWarningClose = useCallback((open: boolean) => {
    if (!open) {
      setShowGlobalWarning(false);
      setPendingAction(null);
      setSelectedFirmId('');
    }
  }, []);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={handleFileChange}
      />
      <div className="flex items-center">
        <Button
          size="sm"
          className="h-8 rounded-r-none text-xs"
          disabled={isCreating}
          onClick={handleUploadDocument}
        >
          <Plus className="h-3.5 w-3.5" />
          {isCreating ? 'Creating...' : 'New Template'}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="h-8 rounded-l-none border-l border-primary-foreground/20 px-1.5"
              disabled={isCreating}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleCreateFormat('HTML')}>
              <CodeBracketIcon className="h-4 w-4" />
              HTML Template
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleCreateFormat('SMS')}>
              <ChatBubbleLeftIcon className="h-4 w-4" />
              SMS Template
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleCreateFormat('EXTERNAL')}>
              <CloudIcon className="h-4 w-4" />
              External
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Global scope warning dialog */}
      <AlertDialog open={showGlobalWarning} onOpenChange={handleWarningClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Global Template Warning</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;re about to create a globally scoped template that could
              impact all firms. Are you sure you want to do this, or did you
              mean to create one for a specific firm?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="global-warning-firm"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-300"
            >
              Scope to a firm instead (optional)
            </label>
            <FirmCombobox
              value={selectedFirmId}
              onChange={setSelectedFirmId}
              className="w-full"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {selectedFirmId ? (
              <Button onClick={handleConfirmWithFirm}>
                Create for Firm
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleConfirmGlobal}>
                Yes, Create Global Template
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Validation Error Modal */}
      <AlertDialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Template is Malformed
            </AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{errorTemplateName}&quot; has issues that need to be fixed before it can be used.
              The template was not created.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2 max-h-[300px] overflow-y-auto">
            {analysisErrors.map((err, idx) => (
              <div key={idx} className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-black uppercase tracking-wider text-red-400">
                    {err.phase}
                  </span>
                </div>
                <p className="text-sm font-semibold text-red-900">{err.message}</p>
                {err.detail && (
                  <p className="text-xs font-medium text-red-700 mt-1 whitespace-pre-wrap">{err.detail}</p>
                )}
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
